// @fluxora/auth — the ROPC provider's session-refresh primitives, without React.
//
// Two callers renew a ROPC session, with DIFFERENT failure policies:
//   - the provider's watchdog (timer / wake / hydrate): a rejected refresh
//     token ENDS the session — that is what signs an operator out when the
//     session really is over;
//   - `refreshSession()` (on demand, e.g. a consumer retrying after a 403 to
//     pick up permissions granted since the token was issued): a failure only
//     answers `false`. It must never sign anyone out — the session it was
//     asked to renew is still valid, it just could not be renewed right now.
//
// Both go through ONE single-flight network request. Keycloak rotates the
// refresh token on use, so two parallel refreshes would race: the loser
// presents a token the winner has already rotated away, is rejected, and the
// watchdog would then end a perfectly good session. Sharing the raw request
// removes the race; each caller applies its own policy to the shared outcome.

/** A renewed session, as the provider persists it. `exp` is epoch seconds. */
export interface RefreshedSession {
  accessToken: string;
  refreshToken?: string;
  exp: number;
}

/** What one refresh request concluded. Policy (clear or not) is the caller's. */
export type RefreshOutcome =
  | { kind: 'renewed'; session: RefreshedSession }
  /** The refresh token was judged and refused (4xx), or the answer was unusable. */
  | { kind: 'rejected' }
  /** The refresh token was never judged: transport failure or a 5xx. */
  | { kind: 'unreachable' };

interface RefreshResponseBody {
  accessToken?: unknown;
  refreshToken?: unknown;
  expiresIn?: unknown;
}

/**
 * One `POST <refreshUrl> {refreshToken}`, classified. No side effects beyond
 * the request itself. `expiryOf` turns the new access token into its `exp`.
 */
export async function requestRefresh(
  refreshUrl: string,
  refreshToken: string,
  expiryOf: (accessToken: string, expiresIn: number | undefined) => number,
  fetchImpl: typeof fetch = fetch,
): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetchImpl(refreshUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return { kind: 'unreachable' };
  }
  if (res.status >= 500) return { kind: 'unreachable' };
  if (!res.ok) return { kind: 'rejected' };
  let body: RefreshResponseBody;
  try {
    body = (await res.json()) as RefreshResponseBody;
  } catch {
    // An unparseable 2xx body says nothing about the refresh token, so it is
    // not a rejection: the watchdog keeps the session, as it always has.
    return { kind: 'unreachable' };
  }
  if (typeof body.accessToken !== 'string' || body.accessToken.length === 0) {
    return { kind: 'rejected' };
  }
  const expiresIn = typeof body.expiresIn === 'number' ? body.expiresIn : undefined;
  return {
    kind: 'renewed',
    session: {
      accessToken: body.accessToken,
      // Keycloak may not rotate: keep the token we presented when none comes back.
      refreshToken: typeof body.refreshToken === 'string' ? body.refreshToken : refreshToken,
      exp: expiryOf(body.accessToken, expiresIn),
    },
  };
}

/**
 * Wrap a request function so concurrent calls share ONE in-flight request.
 * The slot frees when the request settles, so the next call makes a new one.
 */
export function singleFlight<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
): (...args: A) => Promise<R> {
  let inFlight: Promise<R> | null = null;
  return (...args: A): Promise<R> => {
    if (inFlight !== null) return inFlight;
    const attempt = fn(...args).finally(() => {
      inFlight = null;
    });
    inFlight = attempt;
    return attempt;
  };
}

export interface RefreshStoredSessionDeps {
  /** The CURRENT stored session (read at call time, never a stale closure). */
  read: () => { refreshToken?: string } | null;
  /** Persist a renewed session (state + storage). Called ONLY on success. */
  commit: (session: RefreshedSession) => void;
  /** The shared single-flight raw request. */
  request: (refreshToken: string) => Promise<RefreshOutcome>;
}

/**
 * On-demand renewal: `true` when a renewed session is now in place, `false`
 * otherwise. It NEVER clears the session — not on a rejected refresh token,
 * not on a network failure.
 *
 * - No stored session, or one without a refresh token (a test-knob session):
 *   `false`, no request.
 * - The session was signed out while the request was in flight: `false`, and
 *   the renewed session is NOT committed — a sign-out is never undone.
 * - The shared request already renewed the session through the other caller
 *   (the stored refresh token moved on): `true`, without committing twice.
 */
export async function refreshStoredSession(deps: RefreshStoredSessionDeps): Promise<boolean> {
  const presented = deps.read()?.refreshToken;
  if (presented === undefined || presented.length === 0) return false;
  const outcome = await deps.request(presented);
  if (outcome.kind !== 'renewed') return false;
  const now = deps.read();
  if (now === null) return false;
  if (now.refreshToken === presented) deps.commit(outcome.session);
  return true;
}
